import { useRef, useState } from 'react';
import { Button, IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';

import type { PARENToken, RelationalFieldOption } from '../../../types/QueryBuilder';
import { AutocompletePopover } from '../AutocompletePopover';
import { useQueryBuilderContext } from '../context';
import { useFilterBarContext } from '../FilterBarContext';
import { useAutocompleteNavigation } from '../hooks/useAutocompleteNavigation';
import { useQueryBuilderGridItem } from '../hooks/useQueryBuilderGridItem';
import { collectUsedExpressionTypes, dedupeOptionsByValue } from '../utils';

import styles from './LogicParen.module.css';

// Re-export so existing call sites that imported the type from `./tokens/LogicParen`
// keep working — the canonical home is now `../types`, which avoids cycles between
// FilterBar / FilterBarContext / LogicParen.
export type { RelationalFieldOption };

/**
 * Sentinel value used by the type-cell autocomplete's hardcoded "Logical Group"
 * option. Selecting it clears the EXPRESSION's `expressionType`, downgrading the
 * typed group back to a plain `(...)` parenthesised expression.
 */
const GROUP_SENTINEL = '__GROUP__';

/** Display label + description for the always-present Group option in the type cell. */
const GROUP_OPTION_LABEL = 'Logical Group';
const GROUP_OPTION_DESCRIPTION = 'Logical grouping of filter expressions to evaluate first.';

/**
 * Tones assigned to the first N entries in `relationalFieldOptions` (index-based, so
 * the same entry always maps to the same colour regardless of its `value`). Entries
 * past the end of this palette fall through to the default neutral tone.
 */
const RELATIONAL_FIELD_COLOR_PALETTE = [
  'var(--lp-color-data-series-1)',
  'var(--lp-color-data-series-2)',
  'var(--lp-color-data-series-3)',
  'var(--lp-color-data-series-4)',
  'var(--lp-color-text-code-title)',
  'var(--lp-color-text-code-number)',
];

interface LogicParenProps {
  tokenKey: string;
  token: PARENToken;
  onRemove?: () => void;
  removable?: boolean;
  /**
   * The parent EXPRESSION's optional `expressionType` (the identifier glued to the
   * opening paren in the source query, e.g. `anySpan(...)` → `"anySpan"`). When set
   * the open and close parens take on the type's tone (--paren-color) and the close
   * paren renders an additional clickable cell that, on click, opens an autocomplete
   * letting the user pick a different type.
   */
  expressionType?: string;
  /**
   * Options surfaced in the type-cell autocomplete. When omitted, falls back to the
   * surrounding FilterBar's `relationalFieldOptions` (read from FilterBarContext) —
   * if neither is set, no options are shown and the type cell is non-interactive.
   */
  relationalFieldOptions?: RelationalFieldOption[];
}

export function LogicParen({
  tokenKey,
  token,
  onRemove,
  removable,
  expressionType,
  relationalFieldOptions: relationalFieldOptionsProp,
}: LogicParenProps) {
  const { activePopover, setActivePopover, dispatch, parsedQuery } = useQueryBuilderContext();
  const { readonly, gridState, relationalFieldOptions: relationalFieldOptionsCtx } = useFilterBarContext();
  const relationalFieldOptions = relationalFieldOptionsProp ?? relationalFieldOptionsCtx ?? [];
  const usedExpressionTypes = collectUsedExpressionTypes(parsedQuery);
  const { containerProps, cellProps } = useQueryBuilderGridItem(tokenKey, gridState);
  const [activePart, setActivePart] = useState<'remove' | null>(null);
  const typeButtonRef = useRef<HTMLButtonElement>(null);

  const parenChar = token.side === 'open' ? '(' : ')';
  // The type cell is rendered on the close paren whenever the surrounding context
  // configured any relational-field options — including when the expression has no
  // current type. The icon swaps to `filter-list` in that "untyped" state to hint
  // that picking from the menu is what assigns a type.
  // Hide the type badge entirely in readonly mode: the menu it opens is interactive
  // by definition, so a readonly paren should display only the literal character.
  const showTypeBadge = !readonly && token.side === 'close' && relationalFieldOptions.length > 0;
  // Guard by readonly so that a readonly paren rendering the same parsedQuery as
  // an editable one doesn't mirror its open popover — `activePopover` is shared via
  // QueryBuilderContext.
  const isThisActive = !readonly && activePopover?.tokenKey === tokenKey;
  const isTypePopoverOpen = isThisActive && activePopover?.part === 'type';
  // Prefer the option's display label when one is configured for the active type.
  const activeRelationalOption = relationalFieldOptions.find((o) => o.value === expressionType);
  const typeLabel = activeRelationalOption?.displayName ?? expressionType ?? 'untyped';
  const typeIconName = expressionType ? ('filter' as const) : ('filter-list' as const);
  // Compose the hover tooltip: when a typed prefix is active, the first sentence
  // ("<displayName> relational filter.") is rendered bold and is followed by the
  // option's description (when configured); when the cell is in its untyped state
  // (the default "Group" option) the tooltip prompts the user to convert it.
  const typeTooltip = expressionType ? (
    <>
      <strong>{typeLabel} relational filter.</strong>
      {activeRelationalOption?.description ? (
        <>
          <br />
          {activeRelationalOption.description}
        </>
      ) : null}
    </>
  ) : (
    'Convert logical group to relational filter'
  );

  // Color both parens according to the prefix tone. Tones come from the palette in
  // the order the configured relational fields are listed — index 0 → series-1,
  // index 1 → series-2, etc. Entries beyond the palette length (and untyped
  // expressions) fall back to the default neutral tone.
  const typeIndex = expressionType ? relationalFieldOptions.findIndex((o) => o.value === expressionType) : -1;
  const typeColor =
    typeIndex >= 0 && typeIndex < RELATIONAL_FIELD_COLOR_PALETTE.length
      ? RELATIONAL_FIELD_COLOR_PALETTE[typeIndex]
      : undefined;
  const containerStyle = typeColor ? ({ '--paren-color': typeColor } as React.CSSProperties) : undefined;

  function handleTypeSelect(selected: string | number | boolean) {
    // The hardcoded "Group" option clears the prefix, downgrading the group back
    // to an untyped `(...)` expression. Any other selection writes its `value`
    // through as the new prefix — except when that type is already used elsewhere
    // in the query (the option is rendered disabled; this guard catches the
    // keyboard-Enter path that bypasses react-aria's disabled handling).
    if (selected !== GROUP_SENTINEL && selected !== expressionType && usedExpressionTypes.has(String(selected))) {
      return;
    }
    const newType = selected === GROUP_SENTINEL ? undefined : String(selected);
    dispatch({
      type: 'UPDATE_EXPRESSION_TYPE',
      token: token.expression,
      expressionType: newType,
    });
    setActivePopover(null);
  }

  // Lead the autocomplete with a hardcoded "Group" entry that has no associated
  // colour — selecting it clears the typed prefix. Subsequent entries are the
  // configured relational fields, in their original order so the index-based
  // colour palette assignment stays stable.
  //
  // Per-option flags:
  //   • disabled — already in use elsewhere in the query (and not the active
  //     type for this paren, which the user is free to re-pick as a no-op).
  //   • trailingIconName — green check on the option that matches the current
  //     `expressionType`, so the active type reads as "selected" in the menu.
  // Dedupe so the rendered rows, the arrow-key optionCount, and the Enter onSelect
  // index share one list — a relational field option whose value collides with the
  // group sentinel (or another field) would otherwise desync focus from selection.
  const typeAutocompleteOptions = dedupeOptionsByValue([
    {
      value: GROUP_SENTINEL,
      displayName: GROUP_OPTION_LABEL,
      description: GROUP_OPTION_DESCRIPTION,
      // The Logical Group sentinel represents the *untyped* state — when no
      // `expressionType` is set, the cell currently is a Logical Group, so mark
      // the option as the active selection just like a real type would be.
      ...(!expressionType ? { trailingIconName: 'check-circle' as const, trailingIconTone: 'success' as const } : {}),
    },
    // Non-creatable options (creatable === false) still label/scope hydrated groups but are
    // withheld from the change-type menu so no new group of that type can be created.
    ...relationalFieldOptions
      .filter((o) => o.creatable !== false)
      .map((o) => {
        const inUseElsewhere = usedExpressionTypes.has(o.value) && o.value !== expressionType;
        const isCurrent = o.value === expressionType;
        return {
          ...o,
          ...(inUseElsewhere ? { disabled: true } : {}),
          ...(isCurrent ? { trailingIconName: 'check-circle' as const, trailingIconTone: 'success' as const } : {}),
        };
      }),
  ]);

  // Mirrors the LozengeFilter operator-cell wiring: ArrowUp / ArrowDown move the
  // virtual highlight inside the popover, Enter commits the highlighted option,
  // and Escape closes the popover (returning focus to the trigger).
  const { focusedIndex, containerDataProps, handleKeyDown } = useAutocompleteNavigation({
    optionCount: typeAutocompleteOptions.length,
    isOpen: isTypePopoverOpen,
    onSelect: (index) => handleTypeSelect(typeAutocompleteOptions[index].value),
    onClose: () => {
      setActivePopover(null);
      requestAnimationFrame(() => typeButtonRef.current?.focus());
    },
  });

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper exists only to stop click propagation; interactive controls live inside
    <div className={styles.container} onClick={(e) => e.stopPropagation()}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- role="row" is provided via containerProps spread */}
      <div
        {...containerProps}
        {...containerDataProps}
        onKeyDown={isTypePopoverOpen ? handleKeyDown : undefined}
        onKeyDownCapture={(e) => {
          containerProps.onKeyDownCapture?.(e);
          // Intercept Enter before React Aria's Button can stop propagation so
          // committing a highlighted option always fires when the trigger button
          // has focus (mirrors LozengeFilter's op-cell behaviour).
          if (isTypePopoverOpen && e.key === 'Enter' && focusedIndex >= 0) {
            e.preventDefault();
            e.stopPropagation();
            handleTypeSelect(typeAutocompleteOptions[focusedIndex].value);
          }
        }}
        className={styles.logicParen}
        style={containerStyle}
      >
        <div {...cellProps}>
          <span
            className={clsx(styles.paren, {
              [styles.rightParen]: token.side === 'close',
              [styles.cellFocusedPrev]: activePart === 'remove',
            })}
            aria-hidden
          >
            {parenChar}
          </span>
        </div>

        {showTypeBadge && (
          <div {...cellProps}>
            <TooltipTrigger delay={500}>
              <Button
                ref={typeButtonRef}
                data-part="type"
                className={clsx(styles.typeBadge, {
                  [styles.typeBadgeActive]: isTypePopoverOpen,
                })}
                aria-label={`Expression type: ${typeLabel}`}
                onPress={() => {
                  setActivePopover(isTypePopoverOpen ? null : { tokenKey, part: 'type' });
                }}
                onBlur={
                  isTypePopoverOpen
                    ? () => {
                        // Close the type popover when focus leaves the trigger button.
                        // Selecting an option fires onPress on the listbox item and triggers
                        // its own setActivePopover(null), so this handler only needs to clean
                        // up the case where the user tabs / clicks somewhere unrelated.
                        setActivePopover(null);
                      }
                    : undefined
                }
              >
                <span className={styles.typeBadgeIcon} aria-hidden="true">
                  <Icon name={typeIconName} size="small" />
                </span>
              </Button>
              <Tooltip>{typeTooltip}</Tooltip>
            </TooltipTrigger>
          </div>
        )}

        {token.side === 'close' && removable && (
          <div {...cellProps}>
            <TooltipTrigger delay={500}>
              <IconButton
                className={clsx(styles.removeButton, {
                  [styles.cellFocused]: activePart === 'remove',
                })}
                onFocus={() => setActivePart('remove')}
                onBlur={() => setActivePart(null)}
                icon="cancel"
                variant="minimal"
                size="small"
                aria-label="Remove expression group"
                onPress={onRemove}
              />
              <Tooltip>{expressionType ? 'Remove relational field' : 'Remove logical group'}</Tooltip>
            </TooltipTrigger>
          </div>
        )}
      </div>

      {isTypePopoverOpen && (
        <AutocompletePopover
          triggerRef={typeButtonRef}
          isOpen
          onOpenChange={(open) => !open && setActivePopover(null)}
          onSelect={handleTypeSelect}
          options={typeAutocompleteOptions}
          focusedIndex={focusedIndex}
        />
      )}
    </div>
  );
}

LogicParen.displayName = 'LogicParen';
